import { useSafeContext } from '@aspendental/shared-utils/react';
import { createContext, useMemo, useState } from 'react';

import { useAppContext } from './AppContextProvider.ctx';

import { IUser } from '@shared-components-web/constants';
import { FacilityBrand } from '@shared-components-web/gqlConstants';
import useFacilities from '@shared-components-web/services/useFacilities';
import { Maybe, TAGBrand } from '@shared-components-web/types';

export type ExtendedFacilities = ReturnType<typeof useFacilities> & {
	shouldFetchNearbyFacility: boolean;
	initialize: () => void;
};

export const FacilityContext = createContext<ExtendedFacilities>({
	officeInfo: null,
	facilityCode: undefined,
	facilityBrand: FacilityBrand.AspenDental,
	locationState: {
		nearByOfficeLocations: null,
		closestOfficeInfo: null,
		distance: undefined,
		schedulingSystem: null,
		user: {
			coordinates: {
				latitude: 0,
				longitude: 0,
			},
			hasIpBasedCoordinates: false,
		},
	},
	nearbyFacilities: undefined,
	isWaitingForNearbyFacilities: false,
	shouldFetchNearbyFacility: false,
	initialize: () => {
		return null;
	},
});
FacilityContext.displayName = 'FacilityContext';

export interface IFacilityContextProvider {
	appName?: string;
	appVersion?: string;
	brand: TAGBrand;
	user?: Maybe<IUser>;
	facilityCode?: string;
	children: React.ReactNode;
	defaultToCdn?: boolean;
	disableFacilityFetching?: boolean;
}

export function FacilityContextProvider({
	brand,
	user,
	facilityCode,
	children,
	defaultToCdn = false,
	disableFacilityFetching = false,
}: IFacilityContextProvider) {
	const appConfig = useAppContext();
	const { config, environment, appName, appVersion } = appConfig;
	const [shouldFetchNearbyFacility, setShouldFetchNearbyFacility] = useState(!disableFacilityFetching);

	const bffGraphqlUrl =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const facilities = useFacilities(
		appName,
		appVersion,
		brand as unknown as never,
		bffGraphqlUrl,
		config.featureFlags.useFacilityBeffeDataSource,
		user,
		facilityCode,
		defaultToCdn,
		config.featureFlags.transitionallyUseBeffe,
		config.featureFlags?.useSessionStorageFacilityCode,
		shouldFetchNearbyFacility,
		config?.featureFlags?.showSetOfficeButtonInGoogleMapsCards
	);

	const extendedFacilities = useMemo(
		() => ({
			...facilities,
			shouldFetchNearbyFacility,
			initialize: () => {
				if (!shouldFetchNearbyFacility) {
					setShouldFetchNearbyFacility(true);
				}
			},
		}),
		[facilities, shouldFetchNearbyFacility]
	);

	return <FacilityContext.Provider value={extendedFacilities}>{children}</FacilityContext.Provider>;
}

export function useFacilityContext(shouldInitialize?: boolean) {
	const context = useSafeContext(FacilityContext);

	if (shouldInitialize && !context.shouldFetchNearbyFacility) {
		setTimeout(() => context.initialize(), 0);
	}

	return context;
}
